import type { Session } from "@/model/session";
import { useGlobalI18n } from "@/translations/i18n";

import { plusDays, startOfDay, endOfDay } from "./date";
import { toDateString } from "./dateFormat";

interface Range {
  from: Date;
  to: Date;
}

const { t } = useGlobalI18n();

export function sessionExecution(session: Session): Range {
  return { from: sessionStartDate(session), to: plusDays(sessionEndDate(session), -1) };
}

function sessionPlanning(session: Session): Range | undefined {
  return session.planningStart
    ? { from: session.planningStart, to: planningEndDate(session) }
    : undefined;
}

export function rangeText(range: Range) {
  return t("date.range", { from: toDateString(range.from), to: toDateString(range.to) });
}

/**
 * Check if the range is active at the given date.
 *
 * @param range - The range to check.
 * @param now - The date to check.
 * @returns True if the range is active at the given date, false otherwise. Inclusive of the start and end dates.
 */
function isRangeActive(range?: Range, now: Date = new Date()): boolean {
  if (!range) return false;

  const currentDay = startOfDay(now);
  const rangeStart = startOfDay(range.from);
  const rangeEnd = startOfDay(range.to);

  return currentDay >= rangeStart && currentDay <= rangeEnd;
}

export function isExecuting(session: Session) {
  return isRangeActive(sessionExecution(session));
}

export function isPlanning(session: Session) {
  return isRangeActive(sessionPlanning(session));
}

export function sessionStartDate(session: Session) {
  return session.planningStart || session.iterations[0].start;
}

function sessionEndDate(session: Session) {
  return session.iterations[session.iterations.length - 1].end;
}

/**
 * Returns the effective end date of the planning event, which is the later of:
 * - planningEnd (endOfDay)
 *   - if undefined, falls back to day before iteration_start
 * - planningStart (endOfDay)
 */
function planningEndDate(session: Session) {
  const endTime = Math.max(
    session.planningStart?.getTime() ?? 0,
    session.planningEnd?.getTime() || plusDays(session.iterations[0].start, -1).getTime(),
  );
  return endOfDay(new Date(endTime));
}
